// frontend/src/components/BacktestButton.jsx
//
// Gates the "Run Backtest" button when a bot uses indicators that
// runBotLogic.js doesn't have a backtest series computer for.
//
// Why: runBotLogic's ensureOHLCVIndicatorSeries / ensureTickIndicatorSeries
// have explicit `case` handlers per indicator. Anything unmatched falls
// through `default: break` — the indicator key never gets populated, the
// mathjs expression evaluates with NaN, the buy signal silently never
// fires, and the backtest reports "no trades." Looks like a bad strategy,
// is actually a missing series computer. This component prevents the
// silent failure.
//
// Drop-in replacement for your existing backtest button. Reads the bot's
// indicator list from trade_info, checks against the supported set, and
// renders either an enabled button or a disabled button with a clear
// warning panel listing the blocker indicators.

import React, { useMemo } from 'react';
import {
  Button, Tooltip, Alert, AlertTitle, Typography, Box, Chip,
} from '@mui/material';
import { PlayArrow, WarningAmber, BlockOutlined } from '@mui/icons-material';


// ─── Supported indicator catalog ───────────────────────────────────────────
//
// Names match what users put in expressions / what trade_info.indicators[].name
// stores. Comparison is case-insensitive.
//
// To add a new backtestable indicator:
//   1. Add the case to the relevant switch in runBotLogic.js
//      (ensureOHLCVIndicatorSeries / ensureTickIndicatorSeries /
//       ensureFuturesIndicatorSeries)
//   2. Add the name to the list below
// Both must be done — adding only #2 means the gate lets users run a backtest
// that still produces wrong results.

const OHLCV_SUPPORTED = [
  'SMA', 'EMA',
  'RSI', 'StochRSI',
  'ATR', 'StdDev',
  'ADX',
  'MFI', 'OBV', 'VolumeSum', 'Volume',
  'MACD',
  'BollingerBands', 'Bollinger', 'BB', 'BB_Upper', 'BB_Lower',
  'StochasticK', 'Stochastic', 'Stoch',
  'Ichimoku',
  'SuperTrend',
  'Keltner_Upper', 'Keltner_Mid', 'Keltner_Lower',
  'TickVWAP', 'VWAP',
  'Price',
];

const TICK_SUPPORTED = [
  'tickPrice', 'tickDelta', 'tickVolume', 'tickTradeVolume',
  'tickRSI', 'tickVWAP',
  'SMA', 'EMA', 'RSI',
];

const FUTURES_SUPPORTED = [
  'FundingRate', 'MarkPrice', 'IndexPrice',
  'FundingRateEMA', 'FundingRateSMA', 'FundingRateCumulative',
  'OpenInterest', 'OI_Delta', 'OI_SMA', 'OI_EMA', 'OI_RSI',
  'LongShortRatio', 'LongShortRatioEMA',
  'MarkIndexSpread', 'MarkIndexSpreadEMA',
  'Basis', 'BasisPct', 'BasisPctEMA',
];

// Indicators that exist in the live engine but have no backtest series
// computer. Listed explicitly so users get a meaningful error.
const KNOWN_LIVE_ONLY = [
  'LiqPrice', 'LiquidationDistance',  // need user's open position state
  'VWOI',                              // no series computer
  'Fibonacci',                         // one-shot calc, not a series
];

const upper = arr => new Set(arr.map(s => String(s).toUpperCase()));
const OHLCV_SET     = upper(OHLCV_SUPPORTED);
const TICK_SET      = upper(TICK_SUPPORTED);
const FUTURES_SET   = upper(FUTURES_SUPPORTED);
const LIVE_ONLY_SET = upper(KNOWN_LIVE_ONLY);


// ─── Check ─────────────────────────────────────────────────────────────────

function isIndicatorSupported(name, { isTick, hasFuturesData }) {
  if (!name) return { supported: false, reason: 'Indicator has no name' };
  const u = name.toUpperCase();

  if (LIVE_ONLY_SET.has(u)) {
    return {
      supported: false,
      reason: `${name} runs only in live mode (no historical series available)`,
    };
  }

  // Match the path the bot would actually use
  if (isTick ? TICK_SET.has(u) : OHLCV_SET.has(u)) {
    return { supported: true };
  }

  if (FUTURES_SET.has(u)) {
    if (hasFuturesData) return { supported: true };
    return {
      supported: false,
      reason: `${name} requires historical futures data (funding rates, open interest) which is not available for this backtest`,
    };
  }

  return { supported: false, reason: `${name} is not supported in backtest` };
}


function checkBotBacktestable(bot, { hasFuturesData = false } = {}) {
  if (!bot) {
    return { supported: false, message: 'No bot selected', unsupported: [] };
  }

  let trade_info;
  try {
    trade_info = typeof bot.trade_info === 'string'
      ? JSON.parse(bot.trade_info)
      : bot.trade_info || {};
  } catch {
    return { supported: false, message: 'Bot configuration is malformed', unsupported: [] };
  }

  const isTick = String(trade_info.timeframe || '').toLowerCase() === 'tick';
  const indicators = Array.isArray(trade_info.indicators) ? trade_info.indicators : [];

  const unsupported = [];
  const seen = new Set();
  for (const ind of indicators) {
    const name = ind?.name;
    if (!name || seen.has(name)) continue;
    seen.add(name);
    const r = isIndicatorSupported(name, { isTick, hasFuturesData });
    if (!r.supported) unsupported.push({ name, reason: r.reason });
  }

  if (unsupported.length === 0) {
    return { supported: true, unsupported: [] };
  }

  const names = unsupported.map(u => u.name).join(', ');
  const verb = unsupported.length === 1 ? 'is' : 'are';
  return {
    supported: false,
    unsupported,
    message:
      `Backtest unavailable: ${names} ${verb} not supported in backtest mode. ` +
      `Live trading still works for this bot.`,
  };
}


// ─── Component ─────────────────────────────────────────────────────────────

/**
 * Drop-in replacement for "Run Backtest" button.
 *
 * Props:
 *   bot            - the trading_bots row (trade_info can be string or object)
 *   onRun          - called when the user clicks Run
 *   loading        - true while a backtest is in flight
 *   hasFuturesData - true if your backtest API loads historical futures
 *                    metadata (funding/OI). Default false.
 */
export function BacktestButton({ bot, onRun, loading, hasFuturesData = false }) {
  const check = useMemo(
    () => checkBotBacktestable(bot, { hasFuturesData }),
    [bot, hasFuturesData]
  );

  const button = (
    <Button
      variant="contained"
      disabled={!check.supported || loading}
      onClick={onRun}
      startIcon={check.supported ? <PlayArrow /> : <BlockOutlined />}
    >
      {check.supported ? (loading ? 'Running…' : 'Run Backtest') : 'Backtest Unavailable'}
    </Button>
  );

  if (check.supported) return button;

  return (
    <Box>
      <Tooltip title={check.message} arrow placement="top">
        <span>{button}</span>
      </Tooltip>
      <UnsupportedAlert check={check} />
    </Box>
  );
}


/**
 * Reusable warning panel — shows which indicators are blocking the backtest,
 * with hover tooltips explaining why each one is blocked.
 *
 * Use this inline if you want the warning visible even before the user
 * tries to click Run.
 */
export function UnsupportedAlert({ check }) {
  if (!check || check.supported) return null;

  return (
    <Alert severity="warning" icon={<WarningAmber />} sx={{ mt: 2 }}>
      <AlertTitle>Backtest unavailable for this bot</AlertTitle>
      <Typography variant="body2" sx={{ mb: 1 }}>
        {check.message}
      </Typography>
      {check.unsupported.length > 0 && (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 1 }}>
          {check.unsupported.map((u) => (
            <Tooltip key={u.name} title={u.reason} arrow>
              <Chip size="small" label={u.name} color="warning" variant="outlined" />
            </Tooltip>
          ))}
        </Box>
      )}
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5 }}>
        Edit the strategy to use only backtestable indicators (RSI, EMA, MACD,
        BollingerBands, etc.) if you want to run a historical simulation.
      </Typography>
    </Alert>
  );
}


// Exported for tests or for other components that want to gate elsewhere
// (e.g., grey out a "Backtest" tab in the bot detail page).
export { checkBotBacktestable, isIndicatorSupported };

export default BacktestButton;